import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'sex', 'weight', 'height', 'neck', 'waist', 'hip', 'hipSection',
    'unitImperial', 'unitMetric',
    'weightUnit', 'heightUnit', 'neckUnit', 'waistUnit', 'hipUnit',
    'bodyFatPercent', 'category', 'categoryBadge', 'leanMass', 'fatMass',
    'leanMassUnit', 'fatMassUnit',
    'essentialRange', 'athletesRange', 'fitnessRange', 'averageRange', 'obeseRange',
    'insight'
  ]

  connect() {
    this.isMetric = false
    this.calculate()
  }

  setImperial() {
    if (!this.isMetric) return
    this.isMetric = false
    this.updateUnitDisplay()
    this.convertToImperial()
    this.calculate()
  }

  setMetric() {
    if (this.isMetric) return
    this.isMetric = true
    this.updateUnitDisplay()
    this.convertToMetric()
    this.calculate()
  }

  updateUnitDisplay() {
    if (this.isMetric) {
      this.unitMetricTarget.classList.add('bg-gray-900', 'text-white')
      this.unitMetricTarget.classList.remove('bg-white', 'text-gray-900')
      this.unitImperialTarget.classList.remove('bg-gray-900', 'text-white')
      this.unitImperialTarget.classList.add('bg-white', 'text-gray-900')

      this.weightUnitTarget.textContent = 'kg'
      this.heightUnitTarget.textContent = 'cm'
      this.neckUnitTarget.textContent = 'cm'
      this.waistUnitTarget.textContent = 'cm'
      this.hipUnitTarget.textContent = 'cm'
      this.leanMassUnitTarget.textContent = 'kg'
      this.fatMassUnitTarget.textContent = 'kg'
    } else {
      this.unitImperialTarget.classList.add('bg-gray-900', 'text-white')
      this.unitImperialTarget.classList.remove('bg-white', 'text-gray-900')
      this.unitMetricTarget.classList.remove('bg-gray-900', 'text-white')
      this.unitMetricTarget.classList.add('bg-white', 'text-gray-900')

      this.weightUnitTarget.textContent = 'lbs'
      this.heightUnitTarget.textContent = 'in'
      this.neckUnitTarget.textContent = 'in'
      this.waistUnitTarget.textContent = 'in'
      this.hipUnitTarget.textContent = 'in'
      this.leanMassUnitTarget.textContent = 'lbs'
      this.fatMassUnitTarget.textContent = 'lbs'
    }
  }

  convertToMetric() {
    // lbs to kg
    this.weightTarget.value = Math.round(parseFloat(this.weightTarget.value) * 0.453592)
    // inches to cm
    this.heightTarget.value = Math.round(parseFloat(this.heightTarget.value) * 2.54)
    this.neckTarget.value = (parseFloat(this.neckTarget.value) * 2.54).toFixed(1)
    this.waistTarget.value = (parseFloat(this.waistTarget.value) * 2.54).toFixed(1)
    this.hipTarget.value = (parseFloat(this.hipTarget.value) * 2.54).toFixed(1)
  }

  convertToImperial() {
    // kg to lbs
    this.weightTarget.value = Math.round(parseFloat(this.weightTarget.value) / 0.453592)
    // cm to inches
    this.heightTarget.value = Math.round(parseFloat(this.heightTarget.value) / 2.54)
    this.neckTarget.value = (parseFloat(this.neckTarget.value) / 2.54).toFixed(1)
    this.waistTarget.value = (parseFloat(this.waistTarget.value) / 2.54).toFixed(1)
    this.hipTarget.value = (parseFloat(this.hipTarget.value) / 2.54).toFixed(1)
  }

  calculate() {
    const sex = this.getSex()
    const _weight = parseFloat(this.weightTarget.value) || 0
    let height = parseFloat(this.heightTarget.value) || 0
    let neck = parseFloat(this.neckTarget.value) || 0
    let waist = parseFloat(this.waistTarget.value) || 0
    let hip = parseFloat(this.hipTarget.value) || 0

    // Show/hide hip field based on sex
    if (sex === 'female') {
      this.hipSectionTarget.classList.remove('hidden')
    } else {
      this.hipSectionTarget.classList.add('hidden')
    }

    // Update category ranges based on sex
    this.updateCategoryRanges(sex)

    // Convert to inches for calculation if metric
    if (this.isMetric) {
      height /= 2.54
      neck /= 2.54
      waist /= 2.54
      hip /= 2.54
    }

    // Validate inputs
    if (height <= 0 || neck <= 0 || waist <= 0 || (sex === 'female' && hip <= 0)) {
      return
    }

    // Calculate body fat using US Navy Method
    let bodyFatPercent
    if (sex === 'male') {
      // Men: 495 / (1.0324 - 0.19077 * log10(waist - neck) + 0.15456 * log10(height)) - 450
      const circumference = waist - neck
      if (circumference <= 0) return

      bodyFatPercent = 495 / (1.0324 - 0.19077 * Math.log10(circumference) + 0.15456 * Math.log10(height)) - 450
    } else {
      // Women: 495 / (1.29579 - 0.35004 * log10(waist + hip - neck) + 0.22100 * log10(height)) - 450
      const circumference = waist + hip - neck
      if (circumference <= 0) return

      bodyFatPercent = 495 / (1.29579 - 0.35004 * Math.log10(circumference) + 0.22100 * Math.log10(height)) - 450
    }

    // Clamp body fat to reasonable range
    bodyFatPercent = Math.max(2, Math.min(60, bodyFatPercent))

    // Calculate body composition
    // Weight needs to be in the display unit for output
    const displayWeight = parseFloat(this.weightTarget.value) || 0
    const fatMass = displayWeight * (bodyFatPercent / 100)
    const leanMass = displayWeight - fatMass

    // Update display
    this.bodyFatPercentTarget.textContent = bodyFatPercent.toFixed(1)
    this.leanMassTarget.textContent = Math.round(leanMass)
    this.fatMassTarget.textContent = Math.round(fatMass)

    // Determine category
    const category = this.getCategory(bodyFatPercent, sex)
    this.categoryTarget.textContent = category.name
    this.updateCategoryBadge(category)
    this.insightTarget.textContent = category.insight
  }

  getSex() {
    const sexInputs = this.sexTargets
    const checkedInput = sexInputs.find((input) => input.checked)
    return checkedInput ? checkedInput.value : 'male'
  }

  updateCategoryRanges(sex) {
    if (sex === 'male') {
      this.essentialRangeTarget.textContent = '2-5%'
      this.athletesRangeTarget.textContent = '6-13%'
      this.fitnessRangeTarget.textContent = '14-17%'
      this.averageRangeTarget.textContent = '18-24%'
      this.obeseRangeTarget.textContent = '25%+'
    } else {
      this.essentialRangeTarget.textContent = '10-13%'
      this.athletesRangeTarget.textContent = '14-20%'
      this.fitnessRangeTarget.textContent = '21-24%'
      this.averageRangeTarget.textContent = '25-31%'
      this.obeseRangeTarget.textContent = '32%+'
    }
  }

  getCategory(bodyFatPercent, sex) {
    const categories = {
      male: [
        {
          name: 'ESSENTIAL',
          min: 0,
          max: 5,
          color: 'border-red-500 text-red-700',
          insight: 'Your body fat is at essential levels. This is typically only seen in competitive ' +
            'bodybuilders during competition. Maintaining this level long-term is not recommended ' +
            'as it can affect hormone function and overall health.'
        },
        {
          name: 'ATHLETES',
          min: 5,
          max: 13,
          color: 'border-blue-500 text-blue-700',
          insight: 'You have an athletic level of body fat. This range is common among competitive ' +
            'athletes and fitness enthusiasts. You likely have visible muscle definition and ' +
            'excellent physical performance.'
        },
        {
          name: 'FITNESS',
          min: 13,
          max: 17,
          color: 'border-green-500 text-green-700',
          insight: 'Your body fat percentage falls within the fitness range. This level supports ' +
            'athletic performance while maintaining essential body functions. This is a healthy ' +
            'and sustainable range for most active individuals.'
        },
        {
          name: 'AVERAGE',
          min: 17,
          max: 24,
          color: 'border-yellow-500 text-yellow-700',
          insight: 'Your body fat is within the average range for men. While not associated with ' +
            'major health risks, reducing body fat through diet and exercise could improve energy ' +
            'levels and reduce future health risks.'
        },
        {
          name: 'OBESE',
          min: 24,
          max: 100,
          color: 'border-red-600 text-red-800',
          insight: 'Your body fat percentage is in the obese range. This level is associated with ' +
            'increased health risks including heart disease, diabetes, and metabolic disorders. ' +
            'Consider consulting a healthcare provider for a comprehensive health assessment.'
        }
      ],
      female: [
        {
          name: 'ESSENTIAL',
          min: 0,
          max: 13,
          color: 'border-red-500 text-red-700',
          insight: 'Your body fat is at essential levels. This is typically only seen in competitive ' +
            'athletes. Very low body fat in women can affect menstrual function and hormone balance. ' +
            'Consult a healthcare provider if experiencing any symptoms.'
        },
        {
          name: 'ATHLETES',
          min: 13,
          max: 20,
          color: 'border-blue-500 text-blue-700',
          insight: 'You have an athletic level of body fat. This range is common among competitive ' +
            'female athletes and serious fitness enthusiasts. You likely have good muscle definition ' +
            'and excellent physical performance.'
        },
        {
          name: 'FITNESS',
          min: 20,
          max: 24,
          color: 'border-green-500 text-green-700',
          insight: 'Your body fat percentage falls within the fitness range. This is a healthy and ' +
            'sustainable level that supports physical performance while maintaining hormonal balance ' +
            'and essential body functions.'
        },
        {
          name: 'AVERAGE',
          min: 24,
          max: 31,
          color: 'border-yellow-500 text-yellow-700',
          insight: 'Your body fat is within the average range for women. While not associated with ' +
            'major health risks, improving body composition through diet and exercise could enhance ' +
            'energy levels and long-term health.'
        },
        {
          name: 'OBESE',
          min: 31,
          max: 100,
          color: 'border-red-600 text-red-800',
          insight: 'Your body fat percentage is in the obese range. This level is associated with ' +
            'increased health risks including heart disease, diabetes, and metabolic disorders. ' +
            'Consider consulting a healthcare provider for a comprehensive health assessment.'
        }
      ]
    }

    const sexCategories = categories[sex] || categories.male
    const matchedCategory = sexCategories.find((cat) => bodyFatPercent >= cat.min && bodyFatPercent < cat.max)
    return matchedCategory || sexCategories[sexCategories.length - 1]
  }

  updateCategoryBadge(category) {
    const badge = this.categoryBadgeTarget
    // Remove all color classes
    badge.className = 'px-4 py-2 border-2 font-medium text-sm'
    // Add the category-specific color
    const colorClasses = category.color.split(' ')
    colorClasses.forEach((cls) => badge.classList.add(cls))
  }
}
